/**
 * THE ONE LEVEL CURVE. Pure math, no DB, no React — safe to import on client and server.
 *
 * 🚨 XP IS `users.renown` (decimal(10,2), uncapped). Owner decision 2026-08-22: consolidate the three
 * competing currencies onto renown and retire the others as level drivers:
 *   · `favor`  decimal(5,2) — capped at 999.99, so it could never express a level above ~9. That cap
 *              is exactly why the navbar (which read favor/level) showed "New"/low for everyone while
 *              the leaderboard, which reads renown, showed real standing. See the register entry
 *              `level-system-awards-nothing-0820`.
 *   · `exp`    integer — vestigial, never written. This is the column whose max was measured at 0.
 *
 * ⭐ CUMULATIVE, UNCAPPED, TUNABLE IN ONE CONSTANT. The XP required to have REACHED a level is
 *      totalXpForLevel(L) = round(BASE * L^CURVE)
 * with BASE=120, CURVE=1.85. Early levels come fast (L1 in a session, L5 in ~a week of real use),
 * high levels are a long climb (L50 ≈ 167k, L100 ≈ 602k) and there is no maximum — L keeps rising as
 * long as XP does. To rebalance the whole economy, change BASE/CURVE here and nowhere else.
 *
 * ⚠ MONOTONIC BY CONSTRUCTION. levelFromXp uses the analytic inverse as a seed and then corrects for
 * rounding drift, so it can never disagree with totalXpForLevel at a boundary. A test pins the round
 * trip across the full ladder.
 */

export const XP_BASE = 120
export const XP_CURVE = 1.85

/** Cumulative XP needed to have reached `level`. Level 0 costs 0. Uncapped. */
export function totalXpForLevel(level: number): number {
  if (!Number.isFinite(level) || level <= 0) return 0
  return Math.round(XP_BASE * Math.pow(level, XP_CURVE))
}

/**
 * The level a given cumulative XP buys. Never negative, no cap.
 *
 * The analytic seed `(xp/BASE)^(1/CURVE)` lands within a level of the answer; the two short correction
 * loops absorb the ±1 that Math.round on the thresholds can introduce, so this is exact, not close.
 */
export function levelFromXp(xp: number | string | null | undefined): number {
  const x = coerceXp(xp)
  if (x < totalXpForLevel(1)) return 0
  let level = Math.floor(Math.pow(x / XP_BASE, 1 / XP_CURVE))
  if (level < 0) level = 0
  while (totalXpForLevel(level + 1) <= x) level++
  while (level > 0 && totalXpForLevel(level) > x) level--
  return level
}

export interface XpProgress {
  /** Raw cumulative XP (renown). */
  xp: number
  /** Current level (0+). */
  level: number
  /** Cumulative XP at the start of the current level. */
  levelFloorXp: number
  /** Cumulative XP required for the next level. */
  nextLevelXp: number
  /** XP earned inside the current level. */
  intoLevel: number
  /** Total XP span of the current level. */
  levelSpan: number
  /** XP remaining to the next level. */
  toNext: number
  /** Progress through the current level, 0..100. */
  pct: number
}

/** Everything the crest and the profile bar need to render, from one XP number. */
export function xpProgress(xp: number | string | null | undefined): XpProgress {
  const x = coerceXp(xp)
  const level = levelFromXp(x)
  const levelFloorXp = totalXpForLevel(level)
  const nextLevelXp = totalXpForLevel(level + 1)
  const levelSpan = Math.max(1, nextLevelXp - levelFloorXp)
  const intoLevel = Math.max(0, x - levelFloorXp)
  const toNext = Math.max(0, nextLevelXp - x)
  const pct = Math.min(100, Math.max(0, (intoLevel / levelSpan) * 100))
  return { xp: x, level, levelFloorXp, nextLevelXp, intoLevel, levelSpan, toNext, pct }
}

function coerceXp(xp: number | string | null | undefined): number {
  const n = typeof xp === 'number' ? xp : parseFloat(String(xp ?? '0'))
  return Number.isFinite(n) && n > 0 ? n : 0
}
